import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getMerchantHomeErrorMessage, getMerchantStatistics } from "../api/merchant-home.api";
import type { MerchantStatisticsResponse } from "../types/merchant-home-data.types";
import type { MerchantHomeQueryState } from "./merchant-home-query.types";

export const useMerchantStatistics = (): MerchantHomeQueryState<MerchantStatisticsResponse> => {
  const [data, setData] = useState<MerchantStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { run } = useLatestRequest();
  const dataRef = useRef<MerchantStatisticsResponse | null>(null);
  const sessionRef = useRef(0);

  dataRef.current = data;

  const fetchData = useCallback(async () => {
    sessionRef.current += 1;
    const session = sessionRef.current;

    if (dataRef.current !== null) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await run((signal) => getMerchantStatistics({ signal }));
      if (session !== sessionRef.current) return;
      if (result === undefined) return;
      setData(result);
      setError(null);
    } catch (requestError) {
      if (session !== sessionRef.current) return;
      setError(getMerchantHomeErrorMessage(requestError, "Failed to fetch merchant statistics"));
    } finally {
      if (session === sessionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [run]);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => {
      sessionRef.current += 1;
    };
  }, [fetchData]);

  return { data, loading, refreshing, error, retry: refetch, refetch };
};
